// Phase 10B "AutoMind 2050" — three-up summary metrics for the SMS
// automation page. Counts derived client-side from a single page-200
// fetch over the trailing 14 days. New tenants render zeros — that's fine.
import React, { useEffect, useState } from 'react';

import { MetricCard } from '../../components/ui/metric-card';
import { SectionGrid } from '../../components/ui/section-grid';
import { smsFollowupsApi } from '../../api';

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 14;

function fourteenDaysAgoIso() {
  return new Date(Date.now() - WINDOW_DAYS * DAY_MS).toISOString();
}

// Bucket evaluations by day (newest -> oldest) for sparkline rendering.
// Returns an array of length WINDOW_DAYS where index 0 is the oldest day.
function bucketByDay(items, key) {
  const buckets = new Array(WINDOW_DAYS).fill(0);
  const now = Date.now();
  for (const item of items) {
    if (!key(item)) continue;
    const ts = Date.parse(item.created_at || item.sent_at || '');
    if (Number.isNaN(ts)) continue;
    const ageDays = Math.floor((now - ts) / DAY_MS);
    if (ageDays < 0 || ageDays >= WINDOW_DAYS) continue;
    // index 0 = oldest day, index WINDOW_DAYS-1 = today
    const idx = WINDOW_DAYS - 1 - ageDays;
    buckets[idx] += 1;
  }
  return buckets;
}

export default function SummaryCards({ dealerId }) {
  const [counts, setCounts] = useState({ sent: 0, skipped: 0, failed: 0 });
  const [sparklines, setSparklines] = useState({
    sent: null,
    skipped: null,
    failed: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: 200, offset: 0, date_from: fourteenDaysAgoIso() };
    if (dealerId) params.dealer_id = dealerId;
    smsFollowupsApi
      .list(params)
      .then((res) => {
        if (cancelled) return;
        const items = res?.items || [];
        const c = { sent: 0, skipped: 0, failed: 0 };
        for (const item of items) {
          if (item.status === 'sent') c.sent += 1;
          else if (item.status === 'skipped' || item.status === 'blocked') c.skipped += 1;
          else if (item.status === 'failed') c.failed += 1;
        }
        setCounts(c);
        setSparklines({
          sent: bucketByDay(items, (i) => i.status === 'sent'),
          skipped: bucketByDay(
            items,
            (i) => i.status === 'skipped' || i.status === 'blocked'
          ),
          failed: bucketByDay(items, (i) => i.status === 'failed'),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setCounts({ sent: 0, skipped: 0, failed: 0 });
          setSparklines({ sent: null, skipped: null, failed: null });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealerId]);

  // Only show sparkline if there's any signal — flat-zero is just noise.
  const sentSpark =
    sparklines.sent && sparklines.sent.some((v) => v > 0)
      ? sparklines.sent
      : undefined;
  const skippedSpark =
    sparklines.skipped && sparklines.skipped.some((v) => v > 0)
      ? sparklines.skipped
      : undefined;
  const failedSpark =
    sparklines.failed && sparklines.failed.some((v) => v > 0)
      ? sparklines.failed
      : undefined;

  return (
    <SectionGrid cols={3}>
      <MetricCard
        label="Sent (last 14 days)"
        value={counts.sent}
        loading={loading}
        sparkline={sentSpark}
        helperText="Successful outbound"
      />
      <MetricCard
        label="Skipped (last 14 days)"
        value={counts.skipped}
        loading={loading}
        helperText="Includes blocked decisions"
        sparkline={skippedSpark}
        sparklineColor="#71717A"
      />
      <MetricCard
        label="Failed (last 14 days)"
        value={counts.failed}
        loading={loading}
        sparkline={failedSpark}
        sparklineColor="#71717A"
      />
    </SectionGrid>
  );
}
